import React from 'react';
import styled from 'styled-components';
import {
  CalendarToday,
  Person,
  Business,
  Science,
  TrendingUp,
  Public,
  EmojiObjects
} from '@mui/icons-material';
import { formatDate } from '../../utils/dateUtils';
import EventLayout from '../../components/events/EventLayout';
import BackButton from '../../components/events/BackButton';
import EventHero from '../../components/events/EventHero';
import ImageCarousel from '../../components/events/ImageCarousel';
import EventMeta from '../../components/events/EventMeta';
import KeyTakeaways from '../../components/events/KeyTakeaways';

const Conference = () => {
  const eventData = {
    title: 'The Life Sciences Start-up Ecosystem in Israel',
    speaker: 'Dr. Laurent Choppe',
    position: 'Managing Partner at Cukierman & Co. Life Sciences',
    date: '05/12/2024',
    category: 'Academic',
    description: `We were thrilled to host Dr. Laurent Choppe, Managing Partner at Cukierman & Co. Life Sciences, who delivered an inspiring presentation on Israel's life sciences startup ecosystem.`,
    media: [
      { src: '/events/conf1/conf2.jpg', objectFit: 'cover', objectPosition: '50% 20%' },
      { src: '/events/conf1/conf3.jpg', objectFit: 'cover', objectPosition: '50% 70%' },
      { src: '/events/conf1/conf4.jpg', objectFit: 'cover', objectPosition: 'center' }
    ],
    keyTakeaways: [
      {
        icon: <Science style={{ fontSize: '24px' }} />,
        title: 'Unmatched R&D Powerhouse',
        description: 'Israel invests 6% of GDP in R&D, a global benchmark for prioritizing progress. With 91 unicorns, the ecosystem has matured from the "Start-Up Nation" into a vibrant "Scale-Up Nation."',
        color: '#4F8EF7'
      },
      {
        icon: <EmojiObjects style={{ fontSize: '24px' }} />,
        title: 'Life Sciences on the Rise',
        description: 'Over 1,800 active companies and 78,000 professionals are advancing fields like digital health, AI-driven drug discovery, and neurotechnology, driving meaningful progress in healthcare.',
        color: '#9C88FF'
      },
      {
        icon: <Business style={{ fontSize: '24px' }} />,
        title: 'Infrastructure That Inspires',
        description: 'Institutions like the Technion - Israel Institute of Technology and Weizmann Institute of Science provide the backbone for technology transfer and spin-offs, supported by forward-thinking government initiatives like those from the Israel Innovation Authority.',
        color: '#50E3C2'
      },
      {
        icon: <TrendingUp style={{ fontSize: '24px' }} />,
        title: 'Cultural Superpower',
        description: 'A spirit of chutzpah (audacity), resilience, and a problem-solving mindset fuels Israel\'s unstoppable entrepreneurial drive.',
        color: '#FF6B9D'
      },
      {
        icon: <Public style={{ fontSize: '24px' }} />,
        title: 'Global Impact',
        description: 'With over $80B raised internationally, Israeli startups are deeply integrated into global markets, forming meaningful partnerships in the U.S., Europe, and Asia.',
        color: '#FFB74D'
      }
    ]
  };

  const metaItems = [
    {
      icon: <Person style={{ fontSize: '24px' }} />,
      gradient: 'linear-gradient(135deg, #4F8EF7, #9C88FF)',
      title: eventData.speaker,
      subtitle: eventData.position
    },
    {
      icon: <CalendarToday style={{ fontSize: '24px' }} />,
      gradient: 'linear-gradient(135deg, #50E3C2, #4DD0E1)',
      title: formatDate(eventData.date),
      subtitle: 'Conference Date'
    }
  ];

  const ClosingMessage = styled.div`
  background: white;
  border-radius: 12px;
  padding: 40px;
  text-align: center;
  margin-top: 48px;
  margin-bottom: 60px;
  border: 1px solid rgba(0,0,0,0.1);

  h3 {
    font-family: var(--font-serif);
    font-size: 1.5rem;
    color: var(--color-text);
    margin-bottom: 16px;
  }

  p {
    font-size: 1rem;
    color: var(--color-text-light);
    line-height: 1.6;
    max-width: 800px;
    margin: 0 auto;
  }
`;

  return (
    <EventLayout>
      <BackButton />
      <EventHero
        category={eventData.category}
        title={eventData.title}
        gradient="linear-gradient(90deg, #4F8EF7, #9C88FF)"
      >
        <EventMeta items={metaItems} />
        <p style={{
          fontSize: '18px',
          color: '#64748b',
          lineHeight: '1.7',
          marginBottom: '0'
        }}>
          {eventData.description}
        </p>
      </EventHero>

      <ImageCarousel
        media={eventData.media}
        interval={4000}
      />

      <KeyTakeaways items={eventData.keyTakeaways} />

      {/* Closing Message */}
      <ClosingMessage>
        <h3>🚀 What This Means for the Future</h3>
        <p>
          The session reminded us that great innovation isn't just about technology—it's about people, collaboration, and the willingness to push boundaries. We're grateful for the opportunity to learn from Dr. Choppe and the remarkable ecosystem he represents. The future of life sciences is bold, collaborative, and happening right now in Israel!
        </p>
      </ClosingMessage>
    </EventLayout>
  );
};

export default Conference;